import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { PATHS } from "../config/paths";
import { usePermissions } from "../contexts/PermissionContext";

interface RoleGuardProps {
  children: React.ReactNode;
  allowedRoles?: string[]; 
  slug?: string; 
  currentUser: { role: string } | null;
}

/**
 * RoleGuard prevents users from accessing routes they don't have permission for.
 * Now versatile: checks for a slug or a role.
 */
export const RoleGuard: React.FC<RoleGuardProps> = ({ children, allowedRoles, slug, currentUser }) => {
  const location = useLocation();
  const { isAllowed, loading } = usePermissions();

  if (!currentUser) {
    return <Navigate to={PATHS.LOGIN} state={{ from: location }} replace />;
  }

  if (loading) {
    return <div className="h-screen flex items-center justify-center">Authenticating permissions...</div>;
  }

  const userRole = currentUser.role.toLowerCase();
  
  // Superadmin always has access
  if (userRole === "superadmin") return <>{children}</>;

  // Check Slug Permission (New Versatile logic)
  if (slug && isAllowed(slug)) return <>{children}</>;

  // Check Allowed Roles (Legacy logic)
  if (allowedRoles) {
    const lowerAllowedRoles = allowedRoles.map(r => r.toLowerCase());
    if (lowerAllowedRoles.includes(userRole) || lowerAllowedRoles.includes("all")) {
      return <>{children}</>;
    }
  }

  // If both checks fail, redirect to dashboard
  return <Navigate to={PATHS.DASHBOARD} replace />;
};
